/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import PropTypes from 'prop-types';

// Utils & misc
import useOnClickOutside from '../../utils/useOnClickOutside';
import cn from '../../utils/cn';

import style from './Tooltip.module.css';

const Tooltip = ({
  children, handle, placement, className, visibilityTrigger,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      { name: 'arrow', options: { element: arrowElement, padding: 8 } },
      { name: 'offset', options: { offset: [0, 8] } },
    ],
  });
  if (visibilityTrigger === 'click') useOnClickOutside([popperElement, referenceElement], () => setShowTooltip(false));

  const handleClick = () => visibilityTrigger === 'click' && setShowTooltip((prevState) => !prevState);

  const handleMouseEnter = () => visibilityTrigger === 'over' && setShowTooltip(true);

  const handleMouseLeave = () => visibilityTrigger === 'over' && setShowTooltip(false);

  return (
    <>
      <button
        className={style.tooltipHandle}
        type="button"
        ref={setReferenceElement}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {handle}
      </button>
      {showTooltip
        ? ReactDOM.createPortal(
          <div
            className={cn([style.tooltip, className])}
            role="tooltip"
            ref={setPopperElement}
            style={styles.popper}
            {...attributes.popper}
          >
            {children}
            <div
              className={style.tooltipArrow}
              ref={setArrowElement}
              style={{
                ...styles.arrow,
                top: placement.includes('bottom') ? -4 : 0,
                bottom: placement.includes('top') ? -4 : 0,
                right: placement.includes('left') ? -4 : 0,
                left: placement.includes('right') ? -4 : 0,
                transform: `${styles?.arrow?.transform} rotate(45deg)`,
              }}
            />
          </div>,
          document.body,
        )
        : null}
    </>
  );
};

Tooltip.propTypes = {
  children: PropTypes.node.isRequired,
  handle: PropTypes.node.isRequired,
  placement: PropTypes.oneOf([
    'auto',
    'auto-start',
    'auto-end',
    'top',
    'top-start',
    'top-end',
    'bottom',
    'bottom-start',
    'bottom-end',
    'right',
    'right-start',
    'right-end',
    'left',
    'left-start',
    'left-end',
  ]),
  visibilityTrigger: PropTypes.oneOf(['click', 'over']),
  className: PropTypes.string,
};

Tooltip.defaultProps = {
  placement: 'bottom-end',
  visibilityTrigger: 'click',
  className: null,
};

export default Tooltip;
